// =============================================================================
// OrgAvatar - Identità visiva di un'organizzazione
// =============================================================================
// Un'organizzazione si presenta con il logo caricato oppure, in sua assenza,
// con un'icona standard su fondo colorato. Stesso componente ovunque compaia
// il nome di un'organizzazione (switcher, selezione, inviti, pagina pubblica)
// così che l'identità resti riconoscibile.
//
// I nomi icona ammessi sono la whitelist del backend
// (common/constants/tenant-branding.constants.ts): tenere le due liste allineate.
// =============================================================================

import {
  Building2,
  Store,
  Stethoscope,
  HeartPulse,
  Smile,
  Scissors,
  Car,
  Wrench,
  Dumbbell,
  GraduationCap,
  Scale,
  Camera,
  Utensils,
  PawPrint,
  Sparkles,
  Briefcase,
  Home,
  Leaf,
  Palette,
  Music,
  type LucideIcon,
} from 'lucide-react';
import { resolveAssetUrl } from '../../api/client';

export interface OrgBranding {
  logoUrl: string | null;
  icon: string;
  color: string;
}

export const ORG_ICON_REGISTRY: Record<string, LucideIcon> = {
  Building2,
  Store,
  Stethoscope,
  HeartPulse,
  Smile,
  Scissors,
  Car,
  Wrench,
  Dumbbell,
  GraduationCap,
  Scale,
  Camera,
  Utensils,
  PawPrint,
  Sparkles,
  Briefcase,
  Home,
  Leaf,
  Palette,
  Music,
};

export const ORG_ICON_NAMES = Object.keys(ORG_ICON_REGISTRY);

export const DEFAULT_ORG_ICON = 'Building2';
export const DEFAULT_ORG_COLOR = '#10B981';

/// Palette suggerita per l'identità dell'organizzazione (il primo è il brand)
export const ORG_COLOR_PRESETS = [
  '#10B981',
  '#0F766E',
  '#0891B2',
  '#2563EB',
  '#7C3AED',
  '#DB2777',
  '#DC2626',
  '#EA580C',
  '#CA8A04',
  '#65A30D',
  '#475569',
  '#1F2937',
];

export function resolveOrgIcon(name?: string | null): LucideIcon {
  return (name && ORG_ICON_REGISTRY[name]) || ORG_ICON_REGISTRY[DEFAULT_ORG_ICON];
}

type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const SIZE_CLASSES: Record<Size, { box: string; icon: string; radius: string }> = {
  xs: { box: 'w-6 h-6', icon: 'w-3.5 h-3.5', radius: 'rounded-md' },
  sm: { box: 'w-8 h-8', icon: 'w-4 h-4', radius: 'rounded-lg' },
  md: { box: 'w-10 h-10', icon: 'w-5 h-5', radius: 'rounded-lg' },
  lg: { box: 'w-12 h-12', icon: 'w-6 h-6', radius: 'rounded-xl' },
  xl: { box: 'w-16 h-16', icon: 'w-8 h-8', radius: 'rounded-2xl' },
};

interface OrgAvatarProps {
  branding?: OrgBranding | null;
  /// Nome dell'organizzazione: usato per l'alt text del logo
  name?: string | null;
  size?: Size;
  className?: string;
}

export function OrgAvatar({ branding, name, size = 'sm', className = '' }: OrgAvatarProps) {
  const { box, icon, radius } = SIZE_CLASSES[size];
  const logoUrl = resolveAssetUrl(branding?.logoUrl);

  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={name || ''}
        className={`${box} ${radius} object-cover flex-shrink-0 bg-white ring-1 ring-black/5 ${className}`}
      />
    );
  }

  const Icon = resolveOrgIcon(branding?.icon);
  const color = branding?.color || DEFAULT_ORG_COLOR;

  return (
    <div
      className={`${box} ${radius} flex items-center justify-center flex-shrink-0 ${className}`}
      style={{ backgroundColor: hexToRgba(color, 0.12), color }}
      aria-hidden="true"
    >
      <Icon className={icon} />
    </div>
  );
}

/**
 * Variante "piena": icona bianca su fondo pieno del colore scelto. Per le
 * superfici dove l'avatar è l'elemento principale (pagina pubblica, card di
 * selezione dell'organizzazione).
 */
export function OrgAvatarSolid({ branding, name, size = 'lg', className = '' }: OrgAvatarProps) {
  const { box, icon, radius } = SIZE_CLASSES[size];
  const logoUrl = resolveAssetUrl(branding?.logoUrl);

  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={name || ''}
        className={`${box} ${radius} object-cover flex-shrink-0 bg-white shadow-sm ring-1 ring-black/5 ${className}`}
      />
    );
  }

  const Icon = resolveOrgIcon(branding?.icon);
  const color = branding?.color || DEFAULT_ORG_COLOR;

  return (
    <div
      className={`${box} ${radius} flex items-center justify-center flex-shrink-0 text-white shadow-sm ${className}`}
      style={{ backgroundColor: color }}
      aria-hidden="true"
    >
      <Icon className={icon} />
    </div>
  );
}

/// #RRGGBB (o #RGB) → rgba(), per i fondi tenui degli avatar
export function hexToRgba(hex: string, alpha: number): string {
  const normalized = hex.replace('#', '');
  const full =
    normalized.length === 3
      ? normalized.split('').map((c) => c + c).join('')
      : normalized;
  const int = parseInt(full, 16);
  if (Number.isNaN(int) || full.length !== 6) return `rgba(16, 185, 129, ${alpha})`;
  return `rgba(${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}, ${alpha})`;
}
